import * as THREE from 'three';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import Application from '../Application';
import Computer from './Computer';
import MonitorScreen from './MonitorScreen';

const makeWoodTexture = (): THREE.CanvasTexture => {
    const size = 512;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d')!;
    const grad = ctx.createLinearGradient(0, 0, size, 0);
    grad.addColorStop(0, '#4a2e1a');
    grad.addColorStop(0.5, '#5d3a22');
    grad.addColorStop(1, '#3d2614');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);

    for (let i = 0; i < 260; i++) {
        const y = Math.random() * size;
        const len = 80 + Math.random() * 380;
        const x0 = Math.random() * size;
        ctx.strokeStyle = `rgba(${15 + Math.random() * 30},${8 + Math.random() * 14},0,${0.08 + Math.random() * 0.22})`;
        ctx.lineWidth = 0.5 + Math.random() * 1.6;
        ctx.beginPath();
        ctx.moveTo(x0, y);
        ctx.bezierCurveTo(
            x0 + len * 0.3,
            y + (Math.random() - 0.5) * 4,
            x0 + len * 0.7,
            y + (Math.random() - 0.5) * 4,
            x0 + len,
            y + (Math.random() - 0.5) * 6
        );
        ctx.stroke();
    }

    for (let i = 0; i < 6; i++) {
        const x = Math.random() * size;
        const y = Math.random() * size;
        const r = 4 + Math.random() * 16;
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, 'rgba(28,14,4,0.85)');
        g.addColorStop(1, 'rgba(28,14,4,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
    }

    const tex = new THREE.CanvasTexture(c);
    tex.encoding = THREE.sRGBEncoding;
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = 8;
    return tex;
};

/** 与 wood BaseColor 同向的程序化法线贴图，让木纹在掠射角下有微反光起伏 */
const makeWoodNormalTexture = (): THREE.CanvasTexture => {
    const size = 512;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#8080ff'; // 默认法线 (0.5, 0.5, 1)
    ctx.fillRect(0, 0, size, size);

    for (let i = 0; i < 180; i++) {
        const y = Math.random() * size;
        const len = 100 + Math.random() * 360;
        const x0 = Math.random() * size;
        const tone = 100 + Math.floor(Math.random() * 80);
        ctx.strokeStyle = `rgba(${tone},${tone},255,0.4)`;
        ctx.lineWidth = 1 + Math.random() * 2;
        ctx.beginPath();
        ctx.moveTo(x0, y);
        ctx.lineTo(x0 + len, y + (Math.random() - 0.5) * 4);
        ctx.stroke();
    }

    const tex = new THREE.CanvasTexture(c);
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    return tex;
};

const makeRadialGradient = (centerHex: string, edgeHex: string): THREE.CanvasTexture => {
    const size = 512;
    const c = document.createElement('canvas');
    c.width = c.height = size;
    const ctx = c.getContext('2d')!;
    const g = ctx.createRadialGradient(size / 2, size * 0.42, 0, size / 2, size * 0.42, size * 0.7);
    g.addColorStop(0, centerHex);
    g.addColorStop(1, edgeHex);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(c);
    tex.encoding = THREE.sRGBEncoding;
    tex.mapping = THREE.EquirectangularReflectionMapping;
    return tex;
};

/** 后景墙的微渐变贴图（顶部稍亮，底部偏暗） */
const makeWallGradient = (): THREE.CanvasTexture => {
    const w = 64;
    const h = 256;
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const ctx = c.getContext('2d')!;
    const g = ctx.createLinearGradient(0, 0, 0, h);
    g.addColorStop(0, '#3a342c');
    g.addColorStop(0.6, '#28241e');
    g.addColorStop(1, '#1a1815');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    const tex = new THREE.CanvasTexture(c);
    tex.encoding = THREE.sRGBEncoding;
    return tex;
};

export default class World {
    application: Application;
    scene: THREE.Scene;
    computer!: Computer;
    monitorScreen!: MonitorScreen;
    autoZoom = true;
    private ready = false;
    private screenViewGuard = false;

    constructor() {
        this.application = new Application();
        this.scene = this.application.scene;

        this.setupEnvironment();
        this.setupRoom();
        this.setupLights();

        this.computer = new Computer();
        this.monitorScreen = new MonitorScreen();

        this.application.resources.on('ready', () => {
            requestAnimationFrame(() => this.afterModelLoaded());
        });

        this.monitorScreen.on('enterMonitor', () => this.onEnterMonitor());
        this.monitorScreen.on('leftMonitor', () => this.onLeftMonitor());
    }

    private setupEnvironment() {
        const pmrem = new THREE.PMREMGenerator(this.application.renderer.instance);
        const envScene = new RoomEnvironment();
        const envMap = pmrem.fromScene(envScene, 0.04).texture;
        this.scene.environment = envMap;
        // 渐变天空背景，比纯色更有空间感
        this.scene.background = makeRadialGradient('#1c2226', '#0a0d10');
        // 远景雾，增加大气透视
        this.scene.fog = new THREE.Fog(0x0a0d10, 14, 32);
        pmrem.dispose();
    }

    private setupRoom() {
        // 桌面：木纹 BaseColor + 程序化 Normal（增加微反光起伏）
        const woodMap = makeWoodTexture();
        woodMap.repeat.set(2.2, 2.2);
        const woodNormal = makeWoodNormalTexture();
        woodNormal.repeat.set(2.2, 2.2);
        const desk = new THREE.Mesh(
            new THREE.PlaneGeometry(22, 16, 1, 1),
            new THREE.MeshStandardMaterial({
                map: woodMap,
                normalMap: woodNormal,
                normalScale: new THREE.Vector2(0.4, 0.4),
                roughness: 0.55,
                metalness: 0.05,
                envMapIntensity: 0.6,
            })
        );
        desk.rotation.x = -Math.PI / 2;
        desk.position.y = 0;
        desk.receiveShadow = true;
        this.scene.add(desk);

        // 背景墙：暖灰带渐变
        const wallTex = makeWallGradient();
        const wall = new THREE.Mesh(
            new THREE.PlaneGeometry(36, 16),
            new THREE.MeshStandardMaterial({
                map: wallTex,
                roughness: 0.95,
                metalness: 0.0,
            })
        );
        wall.position.set(0, 6, -5.8);
        wall.receiveShadow = true;
        this.scene.add(wall);

        // 黑地板兜底
        const floor = new THREE.Mesh(
            new THREE.PlaneGeometry(40, 40),
            new THREE.MeshStandardMaterial({
                color: 0x070809,
                roughness: 1.0,
                metalness: 0.0,
            })
        );
        floor.rotation.x = -Math.PI / 2;
        floor.position.y = -0.5;
        floor.receiveShadow = true;
        this.scene.add(floor);
    }

    private setupLights() {
        // 极低环境光，让场景靠灯具和 IBL 撑起，更接近 Cycles 烘焙的厚重感
        const ambient = new THREE.AmbientLight(0xffeacc, 0.06);
        this.scene.add(ambient);

        // 主光：暖色，从右上方斜下，带强阴影
        const key = new THREE.DirectionalLight(0xffe7c2, 2.4);
        key.position.set(3.5, 6.2, 3.5);
        key.castShadow = true;
        key.shadow.mapSize.set(2048, 2048);
        key.shadow.camera.near = 0.5;
        key.shadow.camera.far = 20;
        key.shadow.camera.left = -6;
        key.shadow.camera.right = 6;
        key.shadow.camera.top = 6;
        key.shadow.camera.bottom = -6;
        key.shadow.bias = -0.0004;
        key.shadow.normalBias = 0.05;
        key.shadow.radius = 4;
        this.scene.add(key);

        // 副光：从左侧偏冷的反射光
        const fill = new THREE.DirectionalLight(0x6688aa, 0.5);
        fill.position.set(-5, 3.5, 2);
        this.scene.add(fill);

        // 后方边缘光：橙色，把电脑轮廓从背景里拉出来
        const rim = new THREE.SpotLight(0xff7733, 4.0, 10, Math.PI / 5, 0.6, 1.4);
        rim.position.set(-1.5, 3.2, -3);
        rim.target.position.set(0, 1.0, 0);
        this.scene.add(rim);
        this.scene.add(rim.target);

        // 屏幕光：CRT 透出的青绿光，洒在键盘和桌面上
        const screenGlow = new THREE.PointLight(0x55d6c8, 1.6, 3.5, 1.8);
        screenGlow.position.set(0, 1.45, 0.55);
        this.scene.add(screenGlow);

        // 桌面填光：从屏幕方向往下打一点，模拟桌面反射
        const tableBounce = new THREE.PointLight(0x3a6e6c, 0.4, 4.5, 2);
        tableBounce.position.set(0, 0.3, 0.6);
        this.scene.add(tableBounce);
    }

    /** 把屏幕的位置塞一个发光面进去，让 bloom 抓得到（CSS3D iframe 不进 WebGL，bloom 不会作用到它） */
    addScreenBloomEmitter() {
        const fit = this.monitorScreen.getFit();
        if (!fit) return;
        const geom = new THREE.PlaneGeometry(fit.widthMeters * 0.98, fit.heightMeters * 0.98);
        const mat = new THREE.MeshBasicMaterial({
            color: 0x3e9697,
            transparent: true,
            opacity: 0.08,
            blending: THREE.AdditiveBlending,
            depthWrite: false,
            side: THREE.FrontSide,
        });
        const mesh = new THREE.Mesh(geom, mat);
        mesh.position.copy(fit.worldPosition);
        mesh.quaternion.copy(fit.worldQuaternion);
        // 推到屏幕面后面一点，让边缘 bloom 漏出来
        mesh.position.addScaledVector(fit.worldNormal, -0.005);
        mesh.renderOrder = -2;
        this.scene.add(mesh);
    }

    private afterModelLoaded() {
        if (this.ready) return;
        const mesh = this.computer.screenMesh;
        if (!mesh) {
            console.warn('[World] no screen mesh — iframe not bound');
            return;
        }
        this.monitorScreen.bindToScreenMesh(mesh);
        this.addScreenBloomEmitter();
        this.ready = true;
        this.snapToFront(false);
        this.setScreenViewGuard(true);
    }

    setAutoZoom(v: boolean) {
        this.autoZoom = v;
        if (v) {
            this.snapToFront(true);
            this.setScreenViewGuard(true);
        } else {
            this.setScreenViewGuard(false);
        }
    }

    setModelFreeMode(v: boolean) {
        const applied = this.monitorScreen.setModelFreeMode(v);
        if (applied && !v && this.autoZoom) {
            this.snapToFront(true);
            this.setScreenViewGuard(true);
        }
        return applied;
    }

    isModelFreeMode() {
        return this.monitorScreen.isModelFreeMode();
    }

    /**
     * 把相机吸附到屏幕的"理想正面视角"：
     * - 沿屏幕法线后退一定距离
     * - 沿世界 Y 轴抬高一点点，让屏幕看起来略带俯视
     */
    snapToFront(animate = true) {
        const fit = this.monitorScreen.getFit();
        const controls = this.application.camera.controls;
        if (!fit || !controls) return;
        const viewportAspect =
            this.application.sizes.width / Math.max(this.application.sizes.height, 1);
        const portraitPullback =
            viewportAspect < 0.75 ? THREE.MathUtils.lerp(1.32, 1, viewportAspect / 0.75) : 1;
        const dist = Math.max(fit.widthMeters, fit.heightMeters) * 2.6 * portraitPullback;
        const elevation = fit.heightMeters * 0.22;
        const worldUp = new THREE.Vector3(0, 1, 0);
        const camPos = fit.worldPosition
            .clone()
            .addScaledVector(fit.worldNormal, dist)
            .addScaledVector(worldUp, elevation);
        const target = fit.worldPosition.clone();
        controls.setLookAt(
            camPos.x,
            camPos.y,
            camPos.z,
            target.x,
            target.y,
            target.z,
            animate
        );
        if (this.autoZoom) this.setScreenViewGuard(true);
    }

    private onEnterMonitor() {
        if (!this.autoZoom) return;
        if (!this.computer.screenMesh || !this.application.camera.controls) return;
        const fit = this.monitorScreen.getFit();
        if (!fit) return;
        const dist = Math.max(fit.widthMeters, fit.heightMeters) * 1.3;
        const camPos = fit.worldPosition.clone().addScaledVector(fit.worldNormal, dist);
        this.application.camera.controls.setLookAt(
            camPos.x,
            camPos.y,
            camPos.z,
            fit.worldPosition.x,
            fit.worldPosition.y,
            fit.worldPosition.z,
            true
        );
        this.setScreenViewGuard(true);
    }

    private onLeftMonitor() {
        if (!this.autoZoom) return;
        this.snapToFront(true);
    }

    private setScreenViewGuard(enabled: boolean) {
        if (this.screenViewGuard === enabled) return;
        this.screenViewGuard = enabled;
        this.application.camera.setInteractionLocked(enabled);
    }

    update() {
        if (this.ready && this.monitorScreen) this.monitorScreen.update();
    }
}
